import React from 'react';
import moment from 'moment';
import { HStack, Pressable, Text, useColorModeValue } from 'native-base';
import { useDispatch, useSelector } from 'react-redux';
import MaterialIconByName from 'src/components/MaterialIconByName';
import { IStore } from 'src/redux/reducers';
import * as AppAction from 'src/redux/actions/appAction';
import { IDateRange, ISearchQuery } from 'src/models';
import { getText } from 'src/utils/i18n';

type ActiveFilterKey = 'grain' | 'search' | 'date' | 'center' | 'state' | 'district';

interface ActiveFilterChip {
  key: ActiveFilterKey;
  label: string;
}

interface ActiveJobFilterChipsProps {
  justifyContent?: 'center' | 'flex-start';
  marginTop?: number;
}

const isActiveFilterValue = (value?: string) => (
  value !== undefined
  && value !== ''
  && value !== '0'
  && value !== 'all'
);

const getSearchTypeLabel = (type?: string) => {
  if (type === 'jobId') {
    return getText('jobIdLabel') || 'Job ID';
  }
  if (type === 'farmerCode') {
    return 'Farmer Code';
  }
  return getText('searchBy') || 'Search';
};

export default function ActiveJobFilterChips(props: ActiveJobFilterChipsProps) {
  const { justifyContent = 'center', marginTop = 2 } = props;
  const dispatch = useDispatch();

  const grainTypes = useSelector((store: IStore) => store.user.userProfile.response?.grains);
  const centers = useSelector((store: IStore) => store.org.centers.centers);
  const grainIdFilter = useSelector((store: IStore) => store.app.grainIdFilter) || 'all';
  const centerIdFilter = useSelector((store: IStore) => store.app.centerIdFilter) || 'all';
  const stateFilter = useSelector((store: IStore) => store.app.stateFilter) || 'all';
  const districtFilter = useSelector((store: IStore) => store.app.districtFilter) || 'all';
  const searchQuery: ISearchQuery | undefined = useSelector((store: IStore) => store.app.searchQuery);
  const dateRange: IDateRange | undefined = useSelector((store: IStore) => store.app.dateRange);

  const chipBg = useColorModeValue('#EEF2F6', '#1F2937');
  const chipBorder = useColorModeValue('#CBD5E1', '#475569');
  const chipText = useColorModeValue('#1F2937', '#F8FAFC');
  const chipIcon = useColorModeValue('#475569', '#CBD5E1');

  const activeFilters = React.useMemo<ActiveFilterChip[]>(() => {
    const filters: ActiveFilterChip[] = [];

    if (isActiveFilterValue(grainIdFilter)) {
      const grainName = grainTypes?.find((grain) => grain.grainId === grainIdFilter)?.name || grainIdFilter;
      filters.push({
        key: 'grain',
        label: `${getText('grainTypeLabel') || 'Grain Type'}: ${grainName}`,
      });
    }

    if (searchQuery?.query) {
      filters.push({
        key: 'search',
        label: `${getSearchTypeLabel(searchQuery.type)}: ${searchQuery.query}`,
      });
    }

    if (dateRange?.from !== undefined && dateRange?.to !== undefined) {
      const from = moment.unix(dateRange.from).format('D MMM, YY');
      const to = moment.unix(dateRange.to).format('D MMM, YY');
      filters.push({
        key: 'date',
        label: `${from} - ${to}`,
      });
    }

    if (isActiveFilterValue(centerIdFilter)) {
      const centerName = centers?.find((center) => center.centerId === centerIdFilter)?.name || centerIdFilter;
      filters.push({
        key: 'center',
        label: `${getText('centerLabel') || 'Center'}: ${centerName}`,
      });
    }

    if (isActiveFilterValue(stateFilter)) {
      filters.push({
        key: 'state',
        label: `State: ${stateFilter}`,
      });
    }

    if (isActiveFilterValue(districtFilter)) {
      filters.push({
        key: 'district',
        label: `District: ${districtFilter}`,
      });
    }

    return filters;
  }, [centerIdFilter, centers, dateRange, districtFilter, grainIdFilter, grainTypes, searchQuery, stateFilter]);

  const removeFilter = (key: ActiveFilterKey) => {
    switch (key) {
      case 'grain':
        dispatch(AppAction.setGrainIdFilter('all'));
        break;
      case 'search':
        dispatch(AppAction.setSearchQuery(undefined));
        break;
      case 'date':
        dispatch(AppAction.setDateRange(undefined));
        break;
      case 'center':
        dispatch(AppAction.setCenterIdFilter('all'));
        break;
      case 'state':
        dispatch(AppAction.setStateFilter('all'));
        break;
      case 'district':
        dispatch(AppAction.setDistrictFilter('all'));
        break;
      default:
        break;
    }
  };

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <HStack
      mt={marginTop}
      px={2}
      space={2}
      flexWrap="wrap"
      justifyContent={justifyContent}
      alignItems="center">
      {activeFilters.map((filter) => (
        <Pressable
          key={filter.key}
          accessibilityRole="button"
          accessibilityLabel={`Remove ${filter.label} filter`}
          onPress={() => removeFilter(filter.key)}
          _hover={{ opacity: 0.85 }}
          bg={chipBg}
          borderColor={chipBorder}
          borderWidth={1}
          borderRadius={16}
          mb={2}
          px={2.5}
          py={1}>
          <HStack alignItems="center" space={1}>
            <Text
              color={chipText}
              fontSize="xs"
              fontWeight="medium"
              maxW={220}
              numberOfLines={1}>
              {filter.label}
            </Text>
            <MaterialIconByName name="close" size="xs" color={chipIcon} />
          </HStack>
        </Pressable>
      ))}
    </HStack>
  );
}
